import { useMemo, useState } from "react";
import { router } from "@inertiajs/react";
import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import { Input, Select } from "@/components/ui/Field";

type Stage = "blocked" | "ready_to_request" | "awaiting_url" | "ready_to_fund";

type HcbEvent = {
  id: number;
  name: string;
  slug: string;
  location: string;
  starts_at: string | null;
  organizer: string | null;
  organizer_email: string | null;
  hcb_ready: boolean;
  blockers: string[];
  stage: Stage;
  stage_label: string;
  requested_at: string | null;
  hcb_url: string | null;
  synced: boolean;
  sync_error: string | null;
  verified_rsvps: number;
  earned_cents: number;
  outstanding_cents: number;
  slack_channel_url: string | null;
  event_path: string;
};

type Totals = {
  total: number;
  blocked: number;
  ready_to_request: number;
  awaiting_url: number;
  ready_to_fund: number;
  unfundable_cents: number;
};

const STAGE_TONE: Record<Stage, "danger" | "brand" | "info" | "success"> = {
  blocked: "danger",
  ready_to_request: "brand",
  awaiting_url: "info",
  ready_to_fund: "success",
};

function money(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="border-line bg-surface flex min-w-40 flex-1 flex-col gap-1 border-2 p-4">
      <span
        className={`font-display text-heading font-bold ${tone ?? "text-ink"}`}
      >
        {value}
      </span>
      <span className="text-caption text-ink-muted">{label}</span>
    </div>
  );
}

// One city's state. The blockers are the whole point of the row, so they read
// as words an organizer would recognise rather than a count.
function EventRow({ event }: { event: HcbEvent }) {
  const stalled = event.hcb_ready && !event.synced;

  return (
    <div className="border-line/20 flex flex-wrap items-center gap-3 border-b py-3 last:border-b-0">
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-body text-ink truncate font-bold">{event.name}</p>
        <p className="text-caption text-ink-soft truncate">
          {event.location}
          {event.starts_at && ` · ${event.starts_at}`}
          {event.organizer && ` · ${event.organizer}`}
        </p>
        {event.blockers.length > 0 && (
          <p className="text-caption text-sunset-orange">
            Waiting on {event.blockers.join(", ")}
          </p>
        )}
        {event.stage === "awaiting_url" && event.requested_at && (
          <p className="text-caption text-ink-muted">
            Asked {event.requested_at}
          </p>
        )}
        {event.hcb_url && (
          <a
            className="text-caption text-link underline"
            href={event.hcb_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {event.hcb_url.replace("https://", "")}
          </a>
        )}
        {event.sync_error && (
          <p className="text-caption text-sunset-orange truncate">
            Sync failed: {event.sync_error}
          </p>
        )}
      </div>

      <div className="text-caption text-ink-muted shrink-0 text-right">
        <p>
          {event.verified_rsvps} verified · earned {money(event.earned_cents)}
        </p>
        {event.outstanding_cents > 0 && (
          <p>{money(event.outstanding_cents)} owed</p>
        )}
      </div>

      <Badge tone={STAGE_TONE[event.stage]}>{event.stage_label}</Badge>
      {stalled && <Badge tone="warning">Not synced</Badge>}

      {event.hcb_ready && event.stage !== "ready_to_fund" && (
        <Button
          size="sm"
          variant={event.stage === "ready_to_request" ? "primary" : "ghost"}
          onClick={() => {
            if (
              confirm(
                `Push ${event.name} to Airtable? This asks HCB to open their account and emails ${event.organizer ?? "the organizer"}.`,
              )
            )
              router.post(
                `/admin/hcb/${event.id}/push`,
                {},
                { preserveScroll: true },
              );
          }}
        >
          {event.stage === "ready_to_request" ? "Send request" : "Re-send"}
        </Button>
      )}

      <div className="text-caption flex shrink-0 gap-3">
        <a className="text-link underline" href={event.event_path}>
          Page
        </a>
        {event.organizer_email && (
          <a
            className="text-link underline"
            href={`mailto:${event.organizer_email}`}
          >
            Email
          </a>
        )}
        {event.slack_channel_url && (
          <a
            className="text-link underline"
            href={event.slack_channel_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Slack
          </a>
        )}
      </div>
    </div>
  );
}

export default function AdminHcb({
  events,
  totals,
  airtable_enabled,
}: {
  events: HcbEvent[];
  totals: Totals;
  airtable_enabled: boolean;
}) {
  const [filter, setFilter] = useState<Stage | "all">("ready_to_request");
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (filter !== "all" && e.stage !== filter) return false;
      if (!q) return true;
      return (
        e.name.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        (e.organizer ?? "").toLowerCase().includes(q)
      );
    });
  }, [events, filter, query]);

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title="HCB accounts"
          subtitle="An account opens when the base is told to open one. This is who's been told, who's still missing a field, and who owes us a link back."
        />

        <div className="flex flex-wrap gap-3">
          <Stat
            label={`Blocked · of ${totals.total} approved`}
            value={String(totals.blocked)}
            tone="text-danger"
          />
          <Stat
            label="Ready, never asked"
            value={String(totals.ready_to_request)}
            tone={
              totals.ready_to_request > 0 ? "text-carrot-orange" : undefined
            }
          />
          <Stat
            label="Asked, waiting on their link"
            value={String(totals.awaiting_url)}
          />
          <Stat
            label={`Ready to fund · ${money(totals.unfundable_cents)} owed elsewhere`}
            value={String(totals.ready_to_fund)}
            tone="text-leaf"
          />
        </div>

        {!airtable_enabled && (
          <Panel title="airtable-off.txt">
            <p className="text-body text-ink">
              Airtable is not configured here, so nothing can be pushed and no
              account will open. Check AIRTABLE_API_KEY and DRY_RUN.
            </p>
          </Panel>
        )}

        {/* The reason this screen exists. The mirror's own sweep keys on
            "changed since last sync", so a row whose readiness changed after
            its last write is invisible to it forever. */}
        {totals.ready_to_request > 0 && airtable_enabled && (
          <Panel title="never-asked.txt">
            <div className="flex flex-col gap-3">
              <p className="text-body text-ink">
                {totals.ready_to_request}{" "}
                {totals.ready_to_request === 1
                  ? "event has everything HCB needs and has never been asked"
                  : "events have everything HCB needs and have never been asked"}
                . Pushing them re-sends their row to the Club Events base, which
                is what opens the account and emails the organizer.
              </p>
              <div>
                <Button
                  onClick={() => {
                    if (
                      confirm(
                        `Push ${totals.ready_to_request} events to Airtable? Each one emails its organizer to start their HCB account.`,
                      )
                    )
                      router.post("/admin/hcb/push", {}, {});
                  }}
                >
                  Send all {totals.ready_to_request} requests
                </Button>
              </div>
            </div>
          </Panel>
        )}

        <Panel title={`hcb-readiness.csv - ${shown.length}`}>
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-3 pb-3">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by event, city or organizer"
                className="max-w-xs"
                aria-label="Filter events"
              />
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as Stage | "all")}
                className="max-w-xs"
                aria-label="Show"
              >
                <option value="ready_to_request">Ready, never asked</option>
                <option value="awaiting_url">Waiting on their link</option>
                <option value="ready_to_fund">Ready to fund</option>
                <option value="blocked">Blocked</option>
                <option value="all">Everything</option>
              </Select>
            </div>

            {shown.length === 0 ? (
              <EmptyState
                title="Nothing here"
                description="No approved event is at that stage right now."
              />
            ) : (
              shown.map((event) => <EventRow key={event.id} event={event} />)
            )}
          </div>
        </Panel>
      </div>
    </Layout>
  );
}
